import {ApprovalContext, SubmissionApprovalDetails} from '../api';
import {
  ApprovalActionInfo,
  ApprovalStatusBadge,
  ApprovalWorkflowHistoryStage,
  ApprovalWorkflowStage
} from './app.types';

/** Travel approval stages of the chain (StageOrder 1-3). */
export const TRAVEL_APPROVAL_CONTEXTS: ApprovalContext[] =
  [ApprovalContext.Manager, ApprovalContext.Funding, ApprovalContext.OutOfRegion];

// Approvals with no created date sort first, and equal dates keep their original order.
function createdTime(approval: SubmissionApprovalDetails): number {
  const time = new Date(approval.created ?? 0).getTime();
  return isNaN(time) ? 0 : time;
}

/**
 * The approvals making up the fixed workflow chain, ordered by stage then oldest first. The server
 * stamps each one with its stage (stageOrder/stageLabel - Manager, Funding, Out Of Region, Manager
 * Acquittal, Funding Acquittal; see ApprovalWorkflowUtil.StageOrder), leaving both null for contexts
 * outside the chain - PrePayment and OverPayment.
 *
 * Those two are dropped rather than listed last: they record finance processing rather than an
 * approver slot, they have no stage to sit at, and TAStore.OverrideApproversAsync refuses their
 * contexts - so neither the approvals table nor the Edit TA Approvers modal can act on them.
 */
export function chainApprovals(approvals?: Array<SubmissionApprovalDetails> | null): SubmissionApprovalDetails[] {
  return (approvals ?? [])
    .filter(approval => approval.stageOrder != null)
    .sort((first, second) =>
      (first.stageOrder! - second.stageOrder!) || (createdTime(first) - createdTime(second)));
}

/**
 * A pending out of region approval sits with the CEOs as a group, so naming one of them would be
 * misleading. An override reassigns the row to an individual (the server clears its group
 * assignment), and from then on - as once actioned - the actual approver is named, same as every
 * other stage.
 */
function pendingApproverName(approval: SubmissionApprovalDetails): string {
  if (approval.groupApprovers?.length) {
    return (approval.actionContext === ApprovalContext.OutOfRegion
      ? 'CEO Group'
      : approval.displayName) ?? '-';
  }
  return approval.approver?.name ?? '-';
}

function toStage(approval: SubmissionApprovalDetails): ApprovalWorkflowStage {
  const isActioned = !!approval.actionedDate;
  const badge = isActioned
    ? ApprovalActionInfo[approval.actionType ?? ''] ?? ApprovalActionInfo['Pending']
    : ApprovalActionInfo[approval.overdueDate ? 'Overdue' : 'Pending'];
  return {
    label: approval.stageLabel ?? '-',
    context: approval.actionContext!,
    approval: approval,
    status: badge.label,
    severity: badge.severity,
    approverName: isActioned ? (approval.actionEvent?.createdByName ?? '-') : pendingApproverName(approval),
    actionedDate: approval.actionedDate,
  };
}

/**
 * Every approval the submission has held, oldest first - resubmitting a withheld TA adds an
 * approval without removing the actioned one. Only a stage's newest approval is flagged as latest,
 * since that is the only one an override can act on. Cancellation is the exception: its rows are
 * parallel (primary + secondary approver) and never superseded, so every one is current.
 *
 * Only chain approvals reach here, so the Edit TA Approvers modal never lists a PrePayment or
 * OverPayment row - and the isLatest test below, which compares stage orders, stays meaningful.
 */
export function toApprovalWorkflowHistory(approvals?: Array<SubmissionApprovalDetails> | null): ApprovalWorkflowHistoryStage[] {
  const chain = chainApprovals(approvals);
  return chain.map((approval, index) => ({
    ...toStage(approval),
    isLatest: approval.actionContext === ApprovalContext.Cancellation
      || chain[index + 1]?.stageOrder !== approval.stageOrder,
  }));
}

/**
 * The single stage a submission holding a cancellation approval sits at, or undefined when it
 * holds none.
 *
 * Cancellation lives outside the chain: requesting one sweeps the pending chain rows and assigns
 * both approvers a parallel row, neither superseding the other. The rows therefore collapse to one
 * stage, named for every approver still to action it - the client's mirror of the cancellation
 * fallback in ApprovalWorkflowUtil.ResolveOverdueApproverName - or, once actioned, for whoever
 * closed it out.
 */
function toCancellationStage(
    approvals?: Array<SubmissionApprovalDetails> | null): ApprovalWorkflowStage | undefined {
  const rows = (approvals ?? [])
    .filter(approval => approval.actionContext === ApprovalContext.Cancellation)
    .sort((first, second) => createdTime(first) - createdTime(second));
  if (rows.length === 0) {
    return undefined;
  }

  const pending = rows.filter(approval => !approval.actionedDate);
  if (pending.length === 0) {
    return toStage(rows[rows.length - 1]);
  }

  // The server flags every pending row of the overdue context, so the oldest carries the badge.
  return {
    ...toStage(pending[0]),
    approverName: [...new Set(pending.map(pendingApproverName))].join(', '),
  };
}

/**
 * Where the submission actually sits - one stage per lifecycle step, superseded approvals dropped.
 * Actioned stages are green, pending ones grey, and the stage the server flags as overdue orange.
 * Approvals outside the chain (PrePayment/OverPayment) never reach here - toApprovalWorkflowHistory
 * drops them.
 *
 * A submission with a cancellation approval is that stage alone: the cancel flow sits outside the
 * chain and supersedes it, so the settled chain steps say nothing about where the TA is held. The
 * server's staleness rule (ApprovalWorkflowUtil.ResolveOverdue) reads it the same way.
 */
export function toApprovalWorkflow(approvals?: Array<SubmissionApprovalDetails> | null): ApprovalWorkflowStage[] {
  const cancellation = toCancellationStage(approvals);
  return cancellation
    ? [cancellation]
    : toApprovalWorkflowHistory(approvals).filter(stage => stage.isLatest);
}

/**
 * The chain approval the submission currently waits on - the first non-superseded stage still
 * pending. Mirrors ApprovalWorkflowUtil.FindCurrentPendingStage on the server.
 */
export function currentPendingApproval(
    approvals?: Array<SubmissionApprovalDetails> | null): SubmissionApprovalDetails | undefined {
  return toApprovalWorkflow(approvals).find(stage => stage.approval.status !== 'Actioned')?.approval;
}

/**
 * The badge shown against a single approval slot. The server stamps overdueDate only on the pending
 * approval the submission is actually stuck on, so a superseded slot never claims to be overdue.
 */
export function toApprovalStatusBadge(approval: SubmissionApprovalDetails): ApprovalStatusBadge {
  if (approval.status !== 'Actioned') {
    return approval.overdueDate
      ? {label: 'Overdue', severity: 'warn'}
      : {label: 'Pending', severity: 'secondary'};
  }
  switch (approval.actionType) {
    case 'Reject':
      return {label: 'Rejected', severity: 'danger'};
    case 'Withhold':
      // Default severity - the primary coloured badge.
      return {label: 'Withheld', severity: undefined};
    case 'Cancelled':
      return {label: 'Cancelled', severity: 'danger'};
    default:
      return {label: 'Approved', severity: 'success'};
  }
}
